"use client";

import { useEffect, useState } from "react";
import { createBrowserSupabase } from "@/lib/supabase/browser";
import { describeSettingsWriteError } from "@/lib/settingsWriteError";
import { scopeForPath } from "@/lib/supabase/scope";
import { normalizeMetaDomainVerification, normalizeSiteVerificationToken } from "@/lib/seo";
import { getClientStoreId } from "@/lib/supabase/storeContextClient";
import { isCatalogHref } from "@/lib/resellerCatalog";
import {
  DEFAULT_SITE_SETTINGS,
  SITE_SETTING_KEYS,
  mapSettingsRows,
  toWaMeNumber,
  type ProductionCostModeSetting,
  type SiteSettingRow,
  type SiteSettings,
  siteSettingKeysFor,
} from "@/lib/siteSettings";
import { createTtlPromiseCache } from "@/lib/ttlCache";
import { CLIENT_SETTINGS_TTL_MS } from "@/lib/siteSettingsCachePolicy";
import {
  notifySiteSettingsSaved,
  registerSettingsCacheInvalidator,
} from "@/lib/supabase/siteSettingsSync";
import type { DashboardAlerts } from "@/lib/dashboard";
import { clampWeightToleranceGram, couriersKey } from "@/lib/biteship";
import { normalizePixelId } from "@/lib/pixel";
import {
  hasActiveAutomaticExpedition,
  hasActiveManualExpedition,
} from "@/lib/expeditions";
import { fetchActiveExpeditions } from "@/lib/supabase/expeditions";

// ─── Cache satu-sumber (PF-02 audit 2026-08) ─────────────────────────────────
// Dulu TIAP mount hook (useStoreName ×32 call-site, useAdminWhatsapp,
// useStoreBranding, useDashboardTargets) + 13 pemanggil langsung menembakkan
// query site_settings sendiri-sendiri — 2–5 query IDENTIK per halaman, dan
// masing-masing menarik SEMUA key (termasuk JSON besar tutorial_sections/
// home_banners yang lalu dibuang). Kini satu promise-cache modul ber-TTL:
//  • kunci ber-dimensi `${scope}:${storeId}` — scope ikut karena RLS mig 3550
//    menyembunyikan key internal dari non-admin: hasil sesi anon (storefront)
//    dan sesi admin bisa BERBEDA untuk store yang sama, dan navigasi SPA bisa
//    berpindah storefront ↔ /admin dalam satu tab; storeId menjaga isolasi
//    antar toko (cache tidak pernah bocor lintas tenant).
//  • TTL pendek (60 dtk) — perubahan setelan dari admin LAIN tetap terlihat
//    cepat; simpan di tab sendiri langsung terlihat lewat invalidate() di
//    saveSiteSettings (semua penulis 15 key ini bermuara ke sana, termasuk
//    branding.ts).
//  • Kegagalan tidak di-cache (kontrak createTtlPromiseCache) — perilaku lama
//    "coba lagi di mount berikutnya" utuh.
//  • TTL TETAP 60 dtk (`CLIENT_SETTINGS_TTL_MS.siteSettings`). Audit 2026-10-30
//    sempat mengusulkan 5 menit, digugurkan: biaya Dropship/Packing dibaca dari
//    cache ini lalu dikirim KLIEN sebagai baris `manual` ke `approve_invoice`
//    (AdminInvoiceDetail.tsx) — server tak menghitung ulang di jalur itu.
const SETTINGS_CACHE_TTL_MS = CLIENT_SETTINGS_TTL_MS.siteSettings;
const settingsCache = createTtlPromiseCache<SiteSettings>(SETTINGS_CACHE_TTL_MS);

/** Buang cache setelan (semua scope/toko) — panggil setelah menulis setelan. */
export function invalidateSiteSettingsCache(): void {
  settingsCache.invalidate();
}
// Tab lain di browser yang sama ikut membuang cache saat setelan disimpan.
registerSettingsCacheInvalidator(invalidateSiteSettingsCache);

function settingsCacheKey(storeId: string, catalog: boolean): string {
  const scope =
    typeof window === "undefined" ? "store" : scopeForPath(window.location.pathname);
  // Katalog punya entri SENDIRI: isinya sengaja tanpa identitas toko, jadi tak
  // boleh terpakai storefront (dan sebaliknya) dalam satu tab.
  return `${scope}:${catalog ? "katalog:" : ""}${storeId}`;
}

/** Halaman katalog reseller `/toko/…` (white-label) sedang terbuka di tab ini? */
function onCatalogPage(): boolean {
  return typeof window !== "undefined" && isCatalogHref(window.location.pathname);
}

/**
 * Ambil setelan website (15 key yang dipetakan `mapSettingsRows`) → objek
 * SiteSettings. Ber-cache + dedup per (scope, toko); key JSON besar di luar
 * daftar TIDAK ikut terunduh (`.in("key", …)`).
 */
export async function fetchSiteSettings(): Promise<SiteSettings> {
  const db = createBrowserSupabase();
  if (!db) return DEFAULT_SITE_SETTINGS;

  // MULTI-TENANT — FAIL-CLOSED. Sesi anon (login/daftar/storefront tamu) lolos
  // cabang escape RLS dan melihat setelan SEMUA toko; tanpa filter store_id
  // toko kedua menampilkan branding toko pertama. Sejak PK `(store_id, key)`
  // (mig 3160) barisnya bahkan bertumpuk per-key, jadi `mapSettingsRows` akan
  // memungut nomor WhatsApp/nama toko milik tenant SEMBARANG.
  //
  // ⛔ Toko tak ter-resolusi ≠ "pakai toko mana saja" (konvensi CLAUDE.md, PR
  // #571): storeId null (apex platform / registry gagal) → setelan BAWAAN,
  // bukan query tanpa filter. Sekelas dengan kebocoran popup lintas-tenant di
  // `fetchActivePopups`. Panel platform tak memakai setelan per-toko, jadi
  // tidak ada jalur sah yang kehilangan data karena ini.
  const storeId = getClientStoreId();
  if (!storeId) return DEFAULT_SITE_SETTINGS;

  // KATALOG RESELLER: identitas toko induk (nama, logo, WA admin, SEO, asal
  // kirim, …) TIDAK diminta sama sekali — bukan sekadar tak ditampilkan —
  // supaya pembeli reseller tak bisa membacanya di tab jaringan. Lihat
  // `CATALOG_HIDDEN_SETTING_KEYS`.
  const catalog = onCatalogPage();
  return settingsCache.get(settingsCacheKey(storeId, catalog), async () => {
    const { data, error } = await db
      .from("site_settings")
      .select("key, value")
      .eq("store_id", storeId)
      .in("key", siteSettingKeysFor(catalog));
    if (error) throw new Error(error.message);
    return mapSettingsRows((data as SiteSettingRow[] | null) ?? []);
  });
}

/**
 * Simpan setelan (upsert per key). Hanya admin (RLS). Menerima objek parsial —
 * key yang tak disertakan tidak diubah.
 */
export async function saveSiteSettings(
  patch: Partial<SiteSettings>
): Promise<void> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Supabase belum siap.");

  const rows: { key: string; value: string; updated_at: string }[] = [];
  const now = new Date().toISOString();
  if (patch.whatsappNumber !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.whatsappNumber,
      value: patch.whatsappNumber.trim(),
      updated_at: now,
    });
  if (patch.storeName !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.storeName,
      value: patch.storeName.trim(),
      updated_at: now,
    });
  if (patch.storeLogoUrl !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.storeLogoUrl,
      value: patch.storeLogoUrl.trim(),
      updated_at: now,
    });
  if (patch.storePwaIcons !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.storePwaIcons,
      value: patch.storePwaIcons.trim(),
      updated_at: now,
    });
  if (patch.googleSiteVerification !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.googleSiteVerification,
      // Disimpan sudah BERSIH supaya isi database sama dengan yang terbit —
      // pembacanya menormalkan lagi (lihat brandingServer.ts) untuk baris lama.
      value: normalizeSiteVerificationToken(patch.googleSiteVerification),
      updated_at: now,
    });
  if (patch.metaDomainVerification !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.metaDomainVerification,
      // Sama seperti token Google: disimpan sudah bersih (kode saja).
      value: normalizeMetaDomainVerification(patch.metaDomainVerification),
      updated_at: now,
    });
  if (patch.seoTitle !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.seoTitle,
      value: patch.seoTitle.trim(),
      updated_at: now,
    });
  if (patch.seoDescription !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.seoDescription,
      value: patch.seoDescription.trim(),
      updated_at: now,
    });
  if (patch.keepExpiryEnabled !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.keepExpiryEnabled,
      value: patch.keepExpiryEnabled ? "true" : "false",
      updated_at: now,
    });
  if (patch.keepExpiryDays !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.keepExpiryDays,
      value: String(patch.keepExpiryDays),
      updated_at: now,
    });
  if (patch.invoiceDueEnabled !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.invoiceDueEnabled,
      value: patch.invoiceDueEnabled ? "true" : "false",
      updated_at: now,
    });
  if (patch.invoiceDueDays !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.invoiceDueDays,
      value: String(patch.invoiceDueDays),
      updated_at: now,
    });
  if (patch.invoiceAutoAccEnabled !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.invoiceAutoAccEnabled,
      value: patch.invoiceAutoAccEnabled ? "true" : "false",
      updated_at: now,
    });
  if (patch.dropshipFeeEnabled !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.dropshipFeeEnabled,
      value: patch.dropshipFeeEnabled ? "true" : "false",
      updated_at: now,
    });
  if (patch.dropshipFeeAmount !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.dropshipFeeAmount,
      value: String(Math.max(0, Math.round(patch.dropshipFeeAmount))),
      updated_at: now,
    });
  if (patch.dashboardAlertPiutang !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.dashboardAlertPiutang,
      value: String(Math.max(0, Math.round(patch.dashboardAlertPiutang))),
      updated_at: now,
    });
  if (patch.dashboardAlertClosedPoNoOrder !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.dashboardAlertClosedPoNoOrder,
      value: String(Math.max(0, Math.round(patch.dashboardAlertClosedPoNoOrder))),
      updated_at: now,
    });
  if (patch.vapidPublicKey !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.vapidPublicKey,
      value: patch.vapidPublicKey.trim(),
      updated_at: now,
    });
  if (patch.archivedPhotoPurgeEnabled !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.archivedPhotoPurgeEnabled,
      value: patch.archivedPhotoPurgeEnabled ? "true" : "false",
      updated_at: now,
    });
  if (patch.archivedPhotoPurgeDays !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.archivedPhotoPurgeDays,
      value: String(patch.archivedPhotoPurgeDays),
      updated_at: now,
    });
  if (patch.resellerCatalogEnabled !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.resellerCatalogEnabled,
      value: patch.resellerCatalogEnabled ? "true" : "false",
      updated_at: now,
    });
  // Izin clone produk antar tenant (mig 5850). Penegaknya di DB
  // (`clonable_store_products`, `clone_products_from_store`); tanpa baris ini
  // saklarnya cuma hiasan — memantul ke NYALA setelah muat ulang.
  if (patch.productCloneAllowed !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.productCloneAllowed,
      value: patch.productCloneAllowed ? "true" : "false",
      updated_at: now,
    });
  // Mode default Biaya Produksi (mig 4250) — nilainya string enum, bukan
  // boolean, karena kemungkinan mode ketiga (mis. maklon) jauh lebih masuk akal
  // daripada "bukan supplier".
  if (patch.productionCostMode !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.productionCostMode,
      value: patch.productionCostMode,
      updated_at: now,
    });
  // Mode cetak bawaan Detail Tagihan & Surat Jalan (mig 7400) — string enum
  // "regular" | "thermal"; pembacanya (`coercePrintMode`) menjatuhkan nilai
  // asing ke "regular".
  if (patch.printModeInvoice !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.printModeInvoice,
      value: patch.printModeInvoice,
      updated_at: now,
    });
  if (patch.printModePackingList !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.printModePackingList,
      value: patch.printModePackingList,
      updated_at: now,
    });
  // Direktori Mitra Terdekat (mig 4230) — tiga bendera, satu bentuk nilai
  // ("true"/"false") yang sama dengan pembaca SQL.
  if (patch.partnerDirectoryEnabled !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.partnerDirectoryEnabled,
      value: patch.partnerDirectoryEnabled ? "true" : "false",
      updated_at: now,
    });
  if (patch.partnerProductCtaEnabled !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.partnerProductCtaEnabled,
      value: patch.partnerProductCtaEnabled ? "true" : "false",
      updated_at: now,
    });
  if (patch.guestWaOrderEnabled !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.guestWaOrderEnabled,
      value: patch.guestWaOrderEnabled ? "true" : "false",
      updated_at: now,
    });
  if (patch.dropshipShopeeEnabled !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.dropshipShopeeEnabled,
      value: patch.dropshipShopeeEnabled ? "true" : "false",
      updated_at: now,
    });
  if (patch.dropshipShopeeStockBuffer !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.dropshipShopeeStockBuffer,
      value: String(Math.max(0, Math.round(patch.dropshipShopeeStockBuffer))),
      updated_at: now,
    });
  if (patch.dropshipShopeeMarkupMin !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.dropshipShopeeMarkupMin,
      value: String(Math.max(0, Math.round(patch.dropshipShopeeMarkupMin))),
      updated_at: now,
    });
  if (patch.dropshipShopeeMitraOnly !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.dropshipShopeeMitraOnly,
      value: patch.dropshipShopeeMitraOnly ? "true" : "false",
      updated_at: now,
    });
  if (patch.partnerDirectoryStatsEnabled !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.partnerDirectoryStatsEnabled,
      value: patch.partnerDirectoryStatsEnabled ? "true" : "false",
      updated_at: now,
    });
  if (patch.registrationAutoActive !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.registrationAutoActive,
      value: patch.registrationAutoActive ? "true" : "false",
      updated_at: now,
    });
  if (patch.poSurplusAutoReady !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.poSurplusAutoReady,
      value: patch.poSurplusAutoReady ? "true" : "false",
      updated_at: now,
    });
  if (patch.shippingRateEnabled !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.shippingRateEnabled,
      value: patch.shippingRateEnabled ? "true" : "false",
      updated_at: now,
    });
  if (patch.shippingOriginAreaId !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.shippingOriginAreaId,
      value: patch.shippingOriginAreaId.trim(),
      updated_at: now,
    });
  if (patch.shippingOriginDistrictKey !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.shippingOriginDistrictKey,
      value: patch.shippingOriginDistrictKey.trim(),
      updated_at: now,
    });
  if (patch.shippingOriginLabel !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.shippingOriginLabel,
      value: patch.shippingOriginLabel.trim(),
      updated_at: now,
    });
  if (patch.shippingCouriers !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.shippingCouriers,
      // Ditulis lewat `couriersKey` — bentuk yang SAMA dengan yang dipakai
      // route sebagai `shipping_rate_cache.couriers_key` dan sebagai parameter
      // `couriers` ke Biteship. Menyimpan apa adanya ("JNE, jnt,jne") membuat
      // nilai DB dan kunci cache dua bentuk berbeda untuk daftar yang sama.
      value: couriersKey(patch.shippingCouriers),
      updated_at: now,
    });
  // Pixel iklan: disimpan lewat `normalizePixelId` (trim + kapital untuk
  // selain Meta) — bentuk yang SAMA dengan yang divalidasi & di-render
  // storefront, supaya satu ID tak punya dua bentuk tersimpan. "" = mati.
  if (patch.metaPixelId !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.metaPixelId,
      value: normalizePixelId("metaPixelId", patch.metaPixelId),
      updated_at: now,
    });
  if (patch.tiktokPixelId !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.tiktokPixelId,
      value: normalizePixelId("tiktokPixelId", patch.tiktokPixelId),
      updated_at: now,
    });
  if (patch.ga4MeasurementId !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.ga4MeasurementId,
      value: normalizePixelId("ga4MeasurementId", patch.ga4MeasurementId),
      updated_at: now,
    });
  if (patch.gtmContainerId !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.gtmContainerId,
      value: normalizePixelId("gtmContainerId", patch.gtmContainerId),
      updated_at: now,
    });
  if (patch.privacyBannerEnabled !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.privacyBannerEnabled,
      value: patch.privacyBannerEnabled ? "true" : "false",
      updated_at: now,
    });
  if (patch.privacyBannerText !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.privacyBannerText,
      value: patch.privacyBannerText.trim(),
      updated_at: now,
    });
  if (patch.shippingWeightToleranceGram !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.shippingWeightToleranceGram,
      // Dijepit lewat helper yang SAMA dengan pembacanya
      // (`parseShippingOriginSettings`). Menyimpan angka mentah membuat DB
      // memuat nilai yang tak akan pernah dipakai — mis. 1500 tersimpan tapi
      // dibaca 999 — sehingga layar menampilkan angka yang berbeda dari yang
      // dihitung route, persis kelas bug "dua pembaca tak sepakat".
      value: String(clampWeightToleranceGram(patch.shippingWeightToleranceGram)),
      updated_at: now,
    });
  if (patch.multiWarehouseEnabled !== undefined)
    rows.push({
      key: SITE_SETTING_KEYS.multiWarehouseEnabled,
      value: patch.multiWarehouseEnabled ? "true" : "false",
      updated_at: now,
    });
  if (rows.length === 0) return;

  const { error } = await db
    .from("site_settings")
    .upsert(rows, { onConflict: "store_id,key" });
  if (error) throw new Error(describeSettingsWriteError(error));

  // Perubahan harus langsung terlihat di tab ini (uji invalidasi PF-02) —
  // buang seluruh cache; TTL 60 dtk menanggung perangkat lain. Tab lain di
  // browser ini dan cache SERVER (tema/pixel/branding, TTL 600 dtk) dibuang
  // lewat `notifySiteSettingsSaved`.
  invalidateSiteSettingsCache();
  notifySiteSettingsSaved();
}

/**
 * Hook nomor WA toko siap-pakai untuk link wa.me (format 62xxx). Mengembalikan
 * fallback (env/default) seketika, lalu memutakhirkan dari `site_settings`.
 */
export function useAdminWhatsapp(): string {
  const [num, setNum] = useState<string>(
    toWaMeNumber(DEFAULT_SITE_SETTINGS.whatsappNumber)
  );
  useEffect(() => {
    let alive = true;
    fetchSiteSettings()
      .then((s) => {
        const wa = toWaMeNumber(s.whatsappNumber);
        if (alive && wa) setNum(wa);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);
  return num;
}

/**
 * Hook ambang peringatan dashboard (mig 4310). 0 = peringatan mati.
 * Memulangkan bawaan seketika lalu memutakhirkan dari DB — dashboard tak
 * boleh menunggu setelan untuk menampilkan angkanya.
 */
export function useDashboardAlerts(): DashboardAlerts {
  const [a, setA] = useState<DashboardAlerts>({
    piutang: DEFAULT_SITE_SETTINGS.dashboardAlertPiutang,
    closedPoNoOrder: DEFAULT_SITE_SETTINGS.dashboardAlertClosedPoNoOrder,
  });
  useEffect(() => {
    let alive = true;
    fetchSiteSettings()
      .then((s) => {
        if (alive)
          setA({
            piutang: s.dashboardAlertPiutang,
            closedPoNoOrder: s.dashboardAlertClosedPoNoOrder,
          });
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);
  return a;
}

/** Branding toko untuk tampilan: nama + logo (mig 196). */
export type StoreBranding = {
  storeName: string;
  /** URL logo toko; "" bila belum diunggah (pakai fallback inisial/ikon). */
  logoUrl: string;
};

/**
 * Hook branding toko (nama + logo) untuk komponen klien. Mengembalikan default
 * seketika, lalu memutakhirkan dari `site_settings`.
 */
export function useStoreBranding(): StoreBranding {
  const [branding, setBranding] = useState<StoreBranding>({
    storeName: DEFAULT_SITE_SETTINGS.storeName,
    logoUrl: DEFAULT_SITE_SETTINGS.storeLogoUrl,
  });
  useEffect(() => {
    let alive = true;
    fetchSiteSettings()
      .then((s) => {
        if (!alive) return;
        setBranding({
          storeName: s.storeName.trim() || DEFAULT_SITE_SETTINGS.storeName,
          logoUrl: s.storeLogoUrl,
        });
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);
  return branding;
}

/**
 * Hook nama toko untuk tampilan/branding. Mengembalikan default seketika, lalu
 * memutakhirkan dari `site_settings`.
 */
export function useStoreName(): string {
  const [name, setName] = useState<string>(DEFAULT_SITE_SETTINGS.storeName);
  useEffect(() => {
    let alive = true;
    fetchSiteSettings()
      .then((s) => {
        const store = s.storeName.trim();
        if (alive && store) setName(store);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);
  return name;
}

/**
 * Apakah cek ongkir otomatis (mig 3720) siap dipakai di layar pengiriman.
 *
 * FAIL-CLOSED, dan itu yang membuatnya berguna: selama setelan belum terbaca
 * — gagal, lambat, atau toko tak ter-resolusi — jawabannya `false`, sehingga
 * tombol/daftar tarif tak pernah muncul lalu menghilang lagi di depan mata
 * pengguna. Alur ongkir manual persis seperti sebelum integrasi.
 *
 * Syaratnya SAMA dengan gerbang di `/api/shipping/rates`: saklar menyala DAN
 * lokasi toko terisi DAN minimal satu kurir dipilih. Menyalinnya ke sini bukan
 * duplikasi aturan otorisasi (route tetap penjaga sebenarnya) melainkan cara
 * menghindari kelas bug "dua pembaca tak sepakat": tanpa syarat yang sama,
 * tombol "Cek ongkir" akan tampil pada toko yang route-nya pasti menjawab
 * `{enabled:false}`.
 *
 * `fetchSiteSettings` ber-cache 60 dtk & dedup in-flight, jadi memanggil hook
 * ini di beberapa layar sekaligus tidak menambah round-trip.
 */
/**
 * Halaman Cari Mitra (/cari-mitra) toko ini menyala? (mig 4230)
 *
 * Dipakai teks yang menjanjikan "tampil di Cari Mitra" (daftar alamat,
 * pengingat alamat utama): di toko yang mematikan direktorinya janji itu
 * bohong. Default MATI sampai setelan terbaca — teks yang lebih pendek
 * sesaat lebih baik daripada janji yang lalu ditarik.
 */
export function usePartnerDirectoryEnabled(): boolean {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    let alive = true;
    fetchSiteSettings()
      .then((s) => {
        if (alive) setEnabled(s.partnerDirectoryEnabled);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);
  return enabled;
}

/**
 * Tombol "Beli lewat mitra terdekat" di halaman produk menyala? (mig 4230)
 *
 * Default MATI sampai setelan terbaca, bukan menyala: tombol yang berkedip
 * muncul-lalu-hilang pada halaman produk yang sudah tayang lebih mengganggu
 * daripada tombol yang datang seperempat detik terlambat.
 */
export function usePartnerProductCta(): boolean {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    let alive = true;
    fetchSiteSettings()
      .then((s) => {
        if (!alive) return;
        // Dua bendera, dan urutannya bukan selera: mematikan direktori harus
        // ikut mematikan tombolnya, kalau tidak pembeli diantar ke halaman yang
        // menjawab "sementara tidak tersedia".
        setEnabled(s.partnerDirectoryEnabled && s.partnerProductCtaEnabled);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);
  return enabled;
}

/**
 * Tombol "Order via WhatsApp" untuk pengunjung yang belum login menyala?
 *
 * Mulai dari MATI sampai setelan terbaca, walau default setelannya NYALA —
 * dan ketidaksimetrisan itu disengaja. Di toko yang mematikannya, mulai dari
 * "nyala" berarti tombol berkedip muncul-lalu-hilang di halaman produk, dan
 * tamu bisa keburu mengkliknya: order masuk lewat jalur yang pemiliknya
 * sengaja tutup. Sebaliknya keterlambatan seperempat detik tak menelan apa
 * pun — tombolnya toh sudah menunggu sesi pembeli (`customerLoading`) yang
 * jalan paralel dengan fetch ini, dan `fetchSiteSettings` ber-cache 60 dtk
 * + dedup in-flight. Pola sama dengan `usePartnerProductCta` di atas.
 */
export function useGuestWaOrder(): boolean {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    let alive = true;
    fetchSiteSettings()
      .then((s) => {
        if (!alive) return;
        setEnabled(s.guestWaOrderEnabled);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);
  return enabled;
}

/**
 * Dropship Shopee mitra menyala di toko ini? (mig 4720) — HANYA untuk
 * menampilkan/menyembunyikan kartu menu di /account. Gerbang sesungguhnya ada
 * di route `/api/account/dropship-shopee/*` (RPC `dropship_shopee_access`).
 * Default MATI sampai setelan terbaca — alasan yang sama dengan
 * `usePartnerProductCta`.
 */
export function useDropshipShopeeSettings(): { enabled: boolean; mitraOnly: boolean } {
  const [state, setState] = useState({ enabled: false, mitraOnly: true });
  useEffect(() => {
    let alive = true;
    fetchSiteSettings()
      .then((s) => {
        if (!alive) return;
        setState({ enabled: s.dropshipShopeeEnabled, mitraOnly: s.dropshipShopeeMitraOnly });
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);
  return state;
}

/**
 * Nota pengajuan pembeli langsung terbit tanpa Perlu ACC? (mig 4320)
 *
 * Default MATI sampai setelan terbaca — alasan yang sama dengan
 * `usePartnerProductCta` di atas, tapi taruhannya lebih tinggi: janji "nota
 * langsung bisa dibayar" yang berkedip lalu berubah jadi "menunggu ACC" adalah
 * janji uang yang meleset, bukan sekadar tombol yang berkelip.
 */
export function useInvoiceAutoAcc(): boolean {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    let alive = true;
    fetchSiteSettings()
      .then((s) => {
        if (!alive) return;
        setEnabled(s.invoiceAutoAccEnabled);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);
  return enabled;
}

/**
 * Mode DEFAULT kartu Biaya Produksi milik toko (mig 4250) + penanda sudah
 * terbaca. `ready` dipakai layar Detail Produksi untuk MENAHAN render daftar
 * kategori sampai defaultnya diketahui: produk tanpa override akan sekejap
 * menampilkan kategori mode supplier lalu berganti, dan pada kartu berisi angka
 * uang kedipan seperti itu terbaca sebagai data yang berubah sendiri.
 */
export function useProductionCostMode(): {
  mode: ProductionCostModeSetting;
  ready: boolean;
} {
  const [mode, setMode] = useState<ProductionCostModeSetting>(
    DEFAULT_SITE_SETTINGS.productionCostMode
  );
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let alive = true;
    fetchSiteSettings()
      .then((s) => {
        if (!alive) return;
        setMode(s.productionCostMode);
        setReady(true);
      })
      // Gagal baca setelan → tetap `ready` dengan default 'supplier'. Kartu
      // yang menggantung selamanya jauh lebih buruk daripada kartu yang
      // memakai default; override per-produk tetap dihormati.
      .catch(() => {
        if (alive) setReady(true);
      });
    return () => {
      alive = false;
    };
  }, []);
  return { mode, ready };
}

/**
 * Gerbang cek ongkir toko dalam TIGA keadaan: `true` aktif, `false` mati,
 * `null` belum diketahui (masih dimuat, atau gagal dibaca).
 *
 * Keadaan ketiga ada supaya layar bisa berkata "cek ongkir otomatis toko ini
 * mati" tanpa berbohong selama setelannya belum tiba — atau saat setelannya
 * gagal dibaca (keduanya tetap `null`; menuduh setelan mati karena jaringan
 * putus menyuruh admin menyalakan sesuatu yang sudah menyala).
 */
export function useShippingRateStatus(): boolean | null {
  const [ready, setReady] = useState<boolean | null>(null);
  useEffect(() => {
    let alive = true;
    // Dua sumber, karena gerbangnya memang dua hal: setelan asal/saklar ada di
    // `site_settings`, sementara daftar KURIR sejak mig 3980 diturunkan dari
    // ekspedisi aktif (`shipping_couriers` sudah tidak menentukan apa pun).
    // Membaca yang lama di sini akan membuat tombol "Cek ongkir" muncul untuk
    // toko yang tak punya satu pun kurir aktif — lalu route menjawab
    // `{enabled:false}` dan pemakainya melihat daftar tarif kosong tanpa sebab.
    Promise.all([fetchSiteSettings(), fetchActiveExpeditions()])
      .then(([s, expeditions]) => {
        if (!alive) return;
        setReady(
          // Dua jalur, dan yang kedua TIDAK menyentuh Biteship: tarif
          // ekspedisi manual (mig 3990) dihitung dari zona & tangga milik
          // toko. Menggabungkannya dengan syarat Biteship akan menyembunyikan
          // tombol dari toko yang mengantar dengan kurirnya sendiri.
          (s.shippingRateEnabled &&
            s.shippingOriginAreaId.trim() !== "" &&
            hasActiveAutomaticExpedition(expeditions)) ||
            hasActiveManualExpedition(expeditions)
        );
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);
  return ready;
}

/** `useShippingRateStatus` versi ya/tidak — `null` (belum diketahui) = tidak. */
export function useShippingRateEnabled(): boolean {
  return useShippingRateStatus() === true;
}
